import { brandsOpenApi, FIELD_NAME } from '@constants';
import { CAMPAIGN_PRODUCTS_LIST_TYPES, CAMPAIGN_TYPE_ID, TABS_TYPES } from '@enums';
import { axiosBoa } from '@utils';
import _ from 'lodash';
import { getParent, types } from 'mobx-state-tree';
import { getCommissionsByForm } from '../pages/CampaignWizard/utils';
import { Campaign, defaultSelectionTableState, Product, SelectionTable } from './constants';
import { createStoreAction } from './utils';

const MAX_PREVIEW_PRODUCTS_COUNT = 8;
const CATALOG_TYPES_WITH_EXCLUDED_PRODUCT_LIST = [
    CAMPAIGN_TYPE_ID.MY_ENTIRE_CATALOG,
    CAMPAIGN_TYPE_ID.AI_RECOMMENDED,
    CAMPAIGN_TYPE_ID.SPECIFIC_PRODUCT_TYPES,
    CAMPAIGN_TYPE_ID.SPECIFIC_COLLECTIONS,
    CAMPAIGN_TYPE_ID.BEST_SELLERS
];

export const CampaignWizardStore = types
    .model('CampaignWizardStore', {
        campaign: types.maybeNull(Campaign),
        selectionTable: types.optional(SelectionTable, defaultSelectionTableState),
        previewProducts: types.array(Product),
        priorityPending: false,
        previewProductsPending: false,
        productsSelectionPending: false,
        pending: false,
        formSubmitting: false,
        isNextDisabled: true,
        isNextLoading: false
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        },
        get accountProducts() {
            return self.selectionTable.accountProducts.data;
        },
        get accountProductsMeta() {
            return self.selectionTable.accountProducts.meta;
        },
        get allAccountProductsSelected() {
            return self.selectedProductsIds.length === self.accountProducts.length;
        },
        get accountProductsIds() {
            return self.selectionTable.accountProducts.data.map((product) => product.id);
        },
        get campaignProducts() {
            return self.selectionTable.campaignProducts.data;
        },
        get campaignProductsMeta() {
            return self.selectionTable.campaignProducts.meta;
        },
        get allCampaignProductsSelected() {
            return self.selectedProductsIds.length === self.campaignProducts.length;
        },
        get campaignProductsIds() {
            return self.selectionTable.campaignProducts.data.map((product) => product.id);
        },
        get campaignProductsListType() {
            const isExcluded = CATALOG_TYPES_WITH_EXCLUDED_PRODUCT_LIST.includes(self.campaign?.catalogType);

            return isExcluded ? CAMPAIGN_PRODUCTS_LIST_TYPES.EXCLUDED : CAMPAIGN_PRODUCTS_LIST_TYPES.INCLUDED;
        },
        get campaignProductsRealTimeTotalPages() {
            return Math.ceil(
                (self.campaignProductsMeta.total -
                    self.campaignProducts.length +
                    self.selectionTable.selectedProductsIds.length) /
                    self.campaignProductsMeta.perPage
            );
        },
        get campaignPriority() {
            return self.campaign?.priority || 0;
        },
        get userIncludedIds() {
            return self.selectionTable.userIncludedIds;
        },
        get userExcludedIds() {
            return self.selectionTable.userExcludedIds;
        },
        get selectedProducts() {
            return self.selectionTable.accountProducts.data.filter((product) =>
                self.selectedProductsIds.includes(product.id)
            );
        },
        get selectedProductsIds() {
            return self.selectionTable.selectedProductsIds;
        },
        get computedCampaignProductsCount() {
            return self.selectionTable.campaignProducts.computedCount;
        },
        get previewProductsLength() {
            return self.previewProducts.length;
        },
        get currentTabType() {
            return self.selectionTable.currentTabType;
        },
        get searchValue() {
            return self.selectionTable.searchValue;
        },
        get selectedCollectionIds() {
            return self.selectionTable.selectedCollectionIds;
        },
        get selectedProductTypeIds() {
            return self.selectionTable.selectedProductTypeIds;
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            // * Actions for External Usage
            setProductsSelectionPending(pending) {
                self.productsSelectionPending = pending;
            },
            setIsNextDisabled(value) {
                self.isNextDisabled = value;
            },
            setIsNextLoading(value) {
                self.isNextLoading = value;
            },
            setFormSubmitting(value) {
                self.formSubmitting = value;
            },
            setPending(pending) {
                self.pending = pending;
            },
            // * Campaign
            getCampaign: action(function* getCampaign(campaignId) {
                const campaign = yield axiosBoa.get(brandsOpenApi.campaigns.get(campaignId), {
                    params: { include: 'creatives,boost,productTypes,collections' }
                });
                self.campaign = campaign;
            }),
            resetCampaign() {
                self.campaign = null;
            },
            createCampaign: action(function* createCampaign(values) {
                const campaign = yield axiosBoa.post(brandsOpenApi.campaigns.create, values);
                self.campaign = campaign;
                return campaign;
            }),
            updateCampaign: action(function* updateCampaign({ campaignId = self.campaign?.id, values }) {
                const campaign = yield axiosBoa.patch(brandsOpenApi.campaigns.patch(campaignId), values);
                self.campaign = _.merge(self.campaign, campaign);
            }),
            submitCampaign: action(function* submitCampaign({
                campaignId = self.campaign?.id,
                // Defaults are intentionally empty arrays, NOT self.userIncludedIds / self.userExcludedIds.
                // The wizard draft (selectionTable.user*) is cleared by syncSelectionWithServer right
                // before submit, so reading from store at call time would silently send []. Callers that
                // need to forward the user's draft exclusions must capture them BEFORE calling sync and
                // pass them explicitly (see Step5 / CampaignEdit handleSubmit).
                userIncludedIds = [],
                userExcludedIds = []
            } = {}) {
                const response = yield axiosBoa.post(brandsOpenApi.campaigns.submit(campaignId), {
                    userIncludedIds,
                    userExcludedIds
                });
                return response;
            }),
            // * Products
            getAccountProducts: action(
                function* getAccountProducts({
                    campaignId = self.campaign?.id,
                    params = {},
                    withCampaignProductsMeta = false
                } = {}) {
                    self.productsSelectionPending = true;
                    const escapedSearchValue = encodeURIComponent(`${self.searchValue}`);

                    const commonParams = {
                        userIncludedIds: self.userIncludedIds,
                        userExcludedIds: self.userExcludedIds,
                        search: self.searchValue ? `title:${escapedSearchValue}` : undefined,
                        selectedProductTypeIds: self.selectedProductTypeIds,
                        selectedCollectionIds: self.selectedCollectionIds
                    };
                    const accountProductsParams = Object.assign({}, commonParams, params);
                    const campaignProductsParams = Object.assign({}, commonParams, {
                        page: 1,
                        limit: 1,
                        type: self.campaignProductsListType
                    });
                    const [accountProducts, { meta: campaignProductsMeta }] = yield Promise.all([
                        self.fetchProducts(campaignId, accountProductsParams),
                        withCampaignProductsMeta
                            ? self.fetchProducts(campaignId, campaignProductsParams)
                            : Promise.resolve({})
                    ]);
                    self.selectionTable.accountProducts.data = accountProducts.data;
                    self.selectionTable.accountProducts.meta = accountProducts.meta;
                    self.syncSelectedProductsIds();
                    if (withCampaignProductsMeta) {
                        self.selectionTable.campaignProducts.meta.total = campaignProductsMeta.total;
                        self.selectionTable.campaignProducts.computedCount = campaignProductsMeta.total;
                    }
                    self.handleCampaignProductsCount();
                },
                { onFinally: () => (self.productsSelectionPending = false) }
            ),
            getCampaignProducts: action(
                function* getCampaignProducts({
                    campaignId = self.campaign?.id,
                    params = {},
                    withAccountProductsMeta = false
                } = {}) {
                    self.productsSelectionPending = true;
                    const escapedSearchValue = encodeURIComponent(`${self.searchValue}`);

                    const commonParams = {
                        userIncludedIds: self.userIncludedIds,
                        userExcludedIds: self.userExcludedIds,
                        search: self.searchValue ? `title:${escapedSearchValue}` : undefined,
                        selectedProductTypeIds: self.selectedProductTypeIds,
                        selectedCollectionIds: self.selectedCollectionIds
                    };
                    const campaignProductsParams = Object.assign(
                        {},
                        commonParams,
                        { type: self.campaignProductsListType },
                        params
                    );
                    const accountProductsParams = Object.assign({}, commonParams, { page: 1, limit: 1 });
                    const [campaignProducts, accountProducts] = yield Promise.all([
                        self.fetchProducts(campaignId, campaignProductsParams),
                        withAccountProductsMeta
                            ? self.fetchProducts(campaignId, accountProductsParams)
                            : Promise.resolve({})
                    ]);
                    self.selectionTable.campaignProducts.data = campaignProducts.data;
                    self.selectionTable.campaignProducts.meta = campaignProducts.meta;
                    self.selectionTable.campaignProducts.computedCount = campaignProducts.meta.total;
                    self.setSelectedProductsIds(self.campaignProductsIds);
                    if (withAccountProductsMeta) {
                        self.selectionTable.accountProducts.meta.total = accountProducts.meta.total;
                    }
                    self.handleCampaignProductsCount();
                },
                { onFinally: () => (self.productsSelectionPending = false) }
            ),
            getCurrentTabProducts: action(function* getCurrentTabProducts({ params, withAnotherTabMeta = false } = {}) {
                if (self.currentTabType === TABS_TYPES.ACCOUNT_PRODUCTS) {
                    yield self.getAccountProducts({ params, withCampaignProductsMeta: withAnotherTabMeta });
                }
                if (self.currentTabType === TABS_TYPES.CAMPAIGN_PRODUCTS) {
                    yield self.getCampaignProducts({ params, withAccountProductsMeta: withAnotherTabMeta });
                }
            }),
            getPreviewProducts: action(
                function* getPreviewProducts(campaignId = self.campaign?.id) {
                    self.previewProductsPending = true;

                    const params = {
                        userIncludedIds: self.userIncludedIds,
                        userExcludedIds: self.userExcludedIds,
                        withStats: false,
                        limit: MAX_PREVIEW_PRODUCTS_COUNT,
                        type: 4,
                        selectedProductTypeIds: self.selectedProductTypeIds,
                        selectedCollectionIds: self.selectedCollectionIds
                    };
                    const previewProducts = yield self.fetchProducts(campaignId, params);
                    self.previewProducts = previewProducts.data;
                },
                { onFinally: () => (self.previewProductsPending = false) }
            ),
            resetPreviewProducts() {
                self.previewProducts = [];
            },
            resetProducts() {
                if (self.currentTabType === TABS_TYPES.ACCOUNT_PRODUCTS) {
                    self.resetAccountProducts();
                }
                if (self.currentTabType === TABS_TYPES.CAMPAIGN_PRODUCTS) {
                    self.resetCampaignProducts();
                }
            },
            resetAccountProducts() {
                self.selectionTable.accountProducts.meta.page = 1;
                self.selectionTable.accountProducts.meta.totalPages = 1;
                self.selectionTable.accountProducts.data = [];
            },
            resetCampaignProducts() {
                self.selectionTable.campaignProducts.meta.page = 1;
                self.selectionTable.campaignProducts.meta.totalPages = 1;
                self.selectionTable.campaignProducts.data = [];
            },
            resetSelectedProductsIds() {
                self.selectionTable.selectedProductsIds = [];
            },
            setPrimaryImageRestPlaces(productId, imageId) {
                const accountProduct = self.selectionTable.accountProducts.data.find(
                    (product) => product.id === productId
                );
                if (accountProduct) {
                    accountProduct.setPrimaryImageLocally(imageId);
                }
                const campaignProduct = self.selectionTable.campaignProducts.data.find(
                    (product) => product.id === productId
                );
                if (campaignProduct) {
                    campaignProduct.setPrimaryImageLocally(imageId);
                }
                const previewProduct = self.previewProducts.find((product) => product.id === productId);
                if (previewProduct) {
                    previewProduct.setPrimaryImageLocally(imageId);
                }
            },
            // * Selections
            handleProductsSelection(ids) {
                let included;
                let excluded;
                if (
                    CATALOG_TYPES_WITH_EXCLUDED_PRODUCT_LIST.includes(self.campaign.catalogType) &&
                    self.selectionTable.currentTabType === TABS_TYPES.CAMPAIGN_PRODUCTS
                ) {
                    included = _.difference(self.selectionTable.selectedProductsIds, ids);
                    excluded = _.difference(ids, self.selectionTable.selectedProductsIds);
                } else {
                    excluded = _.difference(self.selectionTable.selectedProductsIds, ids);
                    included = _.difference(ids, self.selectionTable.selectedProductsIds);
                }
                self.handleInclusionsAndExclusions({ included, excluded });
                self.handleCampaignComputedProductsCount({ included, excluded });
                self.handleCampaignProductsCount();
                self.setSelectedProductsIds(ids);
            },
            resetProductsSelection() {
                self.selectionTable.userExcludedIds = [];
                self.selectionTable.userIncludedIds = [];
            },
            syncSelectionWithServer: action(function* syncSelectionWithServer() {
                if (
                    self.campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_PRODUCTS ||
                    (self.campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_PRODUCT_TYPES &&
                        !self.selectedProductTypeIds.length) ||
                    (self.campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_COLLECTIONS &&
                        !self.selectedCollectionIds.length) ||
                    self.campaign.catalogType === CAMPAIGN_TYPE_ID.BEST_SELLERS
                ) {
                    yield Promise.all([
                        self.userIncludedIds.length
                            ? axiosBoa.post(
                                  brandsOpenApi.campaigns.products.add(self.campaign.id),
                                  self.userIncludedIds
                              )
                            : undefined,
                        self.userExcludedIds.length
                            ? axiosBoa.delete(brandsOpenApi.campaigns.products.remove(self.campaign.id), {
                                  data: self.userExcludedIds
                              })
                            : undefined
                    ]);
                }
                if (
                    self.campaign.catalogType === CAMPAIGN_TYPE_ID.MY_ENTIRE_CATALOG ||
                    self.campaign.catalogType === CAMPAIGN_TYPE_ID.AI_RECOMMENDED
                ) {
                    yield Promise.all([
                        self.userExcludedIds.length
                            ? axiosBoa.post(
                                  brandsOpenApi.campaigns.products.add(self.campaign.id),
                                  self.userExcludedIds
                              )
                            : undefined,
                        self.userIncludedIds.length
                            ? axiosBoa.delete(brandsOpenApi.campaigns.products.remove(self.campaign.id), {
                                  data: self.userIncludedIds
                              })
                            : undefined
                    ]);
                }
                self.selectionTable.userExcludedIds = [];
                self.selectionTable.userIncludedIds = [];
            }),
            // * Others
            setTabType(tabType) {
                self.selectionTable.currentTabType = tabType;
            },
            setSearchValue(searchValue) {
                self.selectionTable.searchValue = searchValue;
            },
            resetSearch() {
                self.selectionTable.searchValue = '';
            },
            creativeSettingsUpdate: action(function* creativeSettingsUpdate({ values }) {
                const updatedCreative = yield axiosBoa.patch(brandsOpenApi.accounts.creative.update(), values);
                self.campaign.creatives = [updatedCreative];
            }),
            campaignCreativeImageUpload: action(function* campaignCreativeImageUpload({ values }) {
                // Upload only - the URL is applied to the creative by the form's Save.
                return yield axiosBoa.post(brandsOpenApi.accounts.creative.imageUpload(), values, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }),
            calculatePriority: action(
                function* calculatePriority({ campaignId = self.campaign?.id, values, updateInDb = false } = {}) {
                    if (isNaN(campaignId) || Object.is(campaignId, null)) {
                        return;
                    }

                    const nonNullableFields = [
                        FIELD_NAME.ACCOUNT.COLD_CUSTOMER_COMMISSION,
                        FIELD_NAME.ACCOUNT.NEW_CUSTOMER_COMMISSION,
                        FIELD_NAME.ACCOUNT.REST_OF_SITE_COMMISSION,
                        FIELD_NAME.CAMPAIGN.COMMISSION
                    ];
                    const fieldsValid = nonNullableFields.every((field) => values[field] !== null);
                    if (!fieldsValid) return;
                    self.priorityPending = true;

                    const params = {
                        ...values,
                        selectedProductTypeIds: self.selectedProductTypeIds,
                        selectedCollectionIds: self.selectedCollectionIds
                    };

                    if (updateInDb) {
                        params.calcMode = 'persist';
                    }

                    const { priority } = yield axiosBoa.post(
                        brandsOpenApi.campaigns.priority.calculate(campaignId),
                        params
                    );
                    if (self.campaign) {
                        self.campaign.priority = priority;
                    } else {
                        self.campaign = { id: 0, priority };
                    }
                },
                { onFinally: () => (self.priorityPending = false) }
            ),
            setPriorityPending(pending) {
                self.priorityPending = pending;
            },
            updatePriority: action(function* updatePriority({ campaignId = self.campaign?.id, values }) {
                const {
                    priority,
                    boostId,
                    commission,
                    restOfSiteCommission,
                    newCustomerCommission,
                    coldCustomerCommission
                } = yield axiosBoa.patch(
                    brandsOpenApi.campaigns.priority.update(campaignId),
                    getCommissionsByForm(values)
                );
                self.campaign.priority = priority;
                self.campaign.commission = commission;
                self.campaign.boostId = boostId;
                self.root.accountsStore.currentAccount.commission.update({
                    coldCustomer: coldCustomerCommission,
                    newCustomer: newCustomerCommission,
                    restOfSite: restOfSiteCommission
                });
            }),
            // * For internal usage (for now)
            fetchProducts: action(function* fetchProducts(campaignId, params = {}) {
                const responseData = yield axiosBoa.post(brandsOpenApi.campaigns.products.listPost(campaignId), params);
                return responseData;
            }),
            syncSelectedProductsIds() {
                const included = _.intersection(self.accountProductsIds, self.selectionTable.userIncludedIds);
                const excluded = _.intersection(self.accountProductsIds, self.selectionTable.userExcludedIds);
                const result = _.chain(self.accountProducts)
                    .filter((product) => product.inCatalog)
                    .map((product) => product.id)
                    .without(...excluded)
                    .union(included)
                    .value();
                self.selectionTable.selectedProductsIds = result;
            },
            handleInclusionsAndExclusions({ included = [], excluded = [] }) {
                self.selectionTable.userIncludedIds = _.union(self.selectionTable.userIncludedIds, included);
                self.selectionTable.userExcludedIds = _.without(self.selectionTable.userExcludedIds, ...included);
                self.selectionTable.userExcludedIds = _.union(self.selectionTable.userExcludedIds, excluded);
                self.selectionTable.userIncludedIds = _.without(self.selectionTable.userIncludedIds, ...excluded);
            },
            handleCampaignComputedProductsCount({ included = [], excluded = [] }) {
                if (CATALOG_TYPES_WITH_EXCLUDED_PRODUCT_LIST.includes(self.campaign.catalogType)) {
                    self.selectionTable.campaignProducts.computedCount =
                        self.computedCampaignProductsCount - included.length + excluded.length;
                }
                if (self.campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_PRODUCTS) {
                    self.selectionTable.campaignProducts.computedCount =
                        self.computedCampaignProductsCount + included.length - excluded.length;
                }
            },
            handleCampaignProductsCount() {
                self.campaign.productsCount = self.accountProductsMeta.total - self.computedCampaignProductsCount;
            },
            setSelectedProductsIds(ids) {
                self.selectionTable.selectedProductsIds = ids;
            },
            setSelectedCollectionIds(collectionIds) {
                self.selectionTable.selectedCollectionIds = collectionIds;
            },
            setSelectedProductTypeIds(productTypeIds) {
                self.selectionTable.selectedProductTypeIds = productTypeIds;
            },
            resetSelectedCollectionIds() {
                self.selectionTable.selectedCollectionIds = [];
            },
            resetSelectedProductTypesIds() {
                self.selectionTable.selectedProductTypeIds = [];
            },
            syncSelectedCollections: action(function* syncSelectedCollections({
                campaignId = self.campaign?.id,
                collections = self.selectionTable.selectedCollectionIds
            } = {}) {
                if (collections.length && self.campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_COLLECTIONS) {
                    return yield axiosBoa.post(brandsOpenApi.campaigns.sync.post(campaignId), {
                        collections,
                        userIncludedIds: self.userIncludedIds,
                        userExcludedIds: self.userExcludedIds
                    });
                }
            }),
            syncSelectedProductTypes: action(function* syncSelectedProductTypes({
                campaignId = self.campaign?.id,
                productTypes = self.selectionTable.selectedProductTypeIds
            } = {}) {
                if (productTypes.length && self.campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_PRODUCT_TYPES) {
                    return yield axiosBoa.post(brandsOpenApi.campaigns.sync.post(campaignId), {
                        productTypes,
                        userIncludedIds: self.userIncludedIds,
                        userExcludedIds: self.userExcludedIds
                    });
                }
            }),
            afterCreate() {}
        };
    });
